import React, { useState } from 'react';

const WALKTHROUGH_VIDEO_SRC = '/videos/advanced-calculator-walkthrough.mp4';
const WALKTHROUGH_POSTER_SRC = '/videos/advanced-calculator-walkthrough-poster.png';

function trackVideoPlay(placement, calculatorTier) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', 'advanced_walkthrough_video_play', {
    event_category: 'engagement',
    event_label: placement,
    placement,
    calculator_tier: calculatorTier,
    value: 1
  });
}

function WalkthroughVideoPlayer({ placement, calculatorTier, active }) {
  if (!active) {
    return (
      <img
        src={WALKTHROUGH_POSTER_SRC}
        alt="Advanced calculator walkthrough preview"
        className="w-full rounded-lg border border-slate-200 bg-black object-cover"
      />
    );
  }

  return (
    <video
      className="w-full rounded-lg border border-slate-200 bg-black"
      controls
      playsInline
      preload="none"
      poster={WALKTHROUGH_POSTER_SRC}
      onPlay={() => trackVideoPlay(placement, calculatorTier)}
    >
      <source src={WALKTHROUGH_VIDEO_SRC} type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  );
}

export function AdvancedWalkthroughVideo({
  placement = 'advanced_top',
  calculatorTier = 'advanced',
  compact = false
}) {
  const [videoActive, setVideoActive] = useState(false);

  if (compact) {
    return (
      <details
        className="mt-4 rounded-lg border border-slate-200 bg-white/80 p-3"
        onToggle={(event) => {
          if (event.currentTarget.open) {
            setVideoActive(true);
          }
        }}
      >
        <summary className="cursor-pointer text-sm font-bold text-slate-900">
          Watch the Advanced walkthrough before you continue
        </summary>
        <p className="mt-2 mb-3 text-xs text-slate-700">
          See how Advanced tests the same plan against real market history, Age Pension timing,
          Monte Carlo outcomes, and withdrawal strategies.
        </p>
        <WalkthroughVideoPlayer
          placement={placement}
          calculatorTier={calculatorTier}
          active={videoActive}
        />
      </details>
    );
  }

  return (
    <section className="mb-6 overflow-hidden rounded-2xl border border-blue-200 bg-white shadow-md">
      <div className="grid gap-5 p-5 lg:grid-cols-[0.9fr_1.2fr] lg:items-center">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-blue-700">
            New to Advanced?
          </p>
          <h2 className="mb-2 text-2xl font-extrabold text-slate-900">
            Watch how Advanced stress-tests a retirement plan
          </h2>
          <p className="mb-4 text-sm leading-relaxed text-slate-700">
            This walkthrough shows how the calculator uses historical markets, Age Pension rules,
            Monte Carlo simulations, and withdrawal strategies to reveal risks a basic projection can miss.
          </p>
          <ul className="space-y-2 text-sm text-slate-700">
            <li><strong>Historical simulation:</strong> test bad market start dates.</li>
            <li><strong>Income by source:</strong> see super, Age Pension, work, and other income year by year.</li>
            <li><strong>What-if scenarios:</strong> change retirement period, fees, and sequence risk.</li>
          </ul>
        </div>
        <div className="relative">
          {!videoActive ? (
            <button
              type="button"
              className="group relative block w-full overflow-hidden rounded-lg border border-slate-200 bg-black text-left"
              onClick={() => setVideoActive(true)}
              aria-label="Load Advanced calculator walkthrough video"
            >
              <img
                src={WALKTHROUGH_POSTER_SRC}
                alt="Advanced calculator walkthrough preview"
                className="w-full object-cover opacity-90 transition group-hover:opacity-100"
              />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="rounded-full bg-white/90 px-4 py-2 text-sm font-bold text-slate-900 shadow-md">
                  Play walkthrough
                </span>
              </span>
            </button>
          ) : (
            <WalkthroughVideoPlayer
              placement={placement}
              calculatorTier={calculatorTier}
              active
            />
          )}
        </div>
      </div>
    </section>
  );
}

export default AdvancedWalkthroughVideo;
